import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthContext } from './useAuthContext';
import { useKeyboardShortcutsDialog } from './useKeyboardShortcutsDialog';
import { useNavigationContext } from './useNavigationContext';
import { getAllStaticCommands, searchWorkflows, searchComponents } from '../config/commands';
import { filterCommandsByRules, getRecentCommands } from '../lib/commandPaletteUtils';
import type { Command } from '../types/commands';

/**
 * Hook to get filtered commands and trigger dynamic searches
 *
 * @param searchQuery - User input for search
 * @param callbacks - Callbacks for command actions
 */
export function useCommands(
  searchQuery: string,
  callbacks: {
    openInviteModal: () => void;
  },
) {
  const { logout } = useAuthContext();
  const navigate = useNavigate();
  const { open: openKeyboardShortcuts } = useKeyboardShortcutsDialog();
  const { context: navContext } = useNavigationContext();

  // Get and filter static commands based on visibility rules
  const staticCommands = useMemo(() => {
    const allCommands = getAllStaticCommands(navigate, {
      openInviteModal: callbacks.openInviteModal,
      openKeyboardShortcuts,
      logout,
    });
    return filterCommandsByRules(allCommands, navContext);
  }, [navContext, navigate, callbacks.openInviteModal, openKeyboardShortcuts, logout]);

  // Dynamic search for workflows (only if query > 2 chars)
  const {
    data: workflows = [],
    isLoading: loadingWorkflows,
    error: workflowsError,
  } = useQuery({
    queryKey: ['command-search-workflows', searchQuery],
    queryFn: () => searchWorkflows(searchQuery, navigate),
    enabled: searchQuery.length > 2,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });

  // Dynamic search for components (only if query > 2 chars)
  const {
    data: components = [],
    isLoading: loadingComponents,
    error: componentsError,
  } = useQuery({
    queryKey: ['command-search-components', searchQuery],
    queryFn: () => searchComponents(searchQuery, navigate),
    enabled: searchQuery.length > 2,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  // Get recent commands
  const recentCommands = useMemo(() => {
    const recent = getRecentCommands();
    // Filter recent commands by current visibility rules
    return filterCommandsByRules(recent, navContext);
  }, [navContext]);

  return {
    staticCommands,
    dynamicCommands: [...workflows, ...components] as Command[],
    recentCommands,
    isLoading: loadingWorkflows || loadingComponents,
    errors: {
      workflows: workflowsError,
      components: componentsError,
    },
  };
}
